import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "bg-surface-2/60 border-metallic text-white placeholder:text-slate-500";

export default function BookingForm({ defaultVehicle = "", onDone }) {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: defaultVehicle,
    preferred_date: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/vehicles", { params: { status: "tillganglig" } })
      .then((r) => setVehicles(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (defaultVehicle) setForm((f) => ({ ...f, vehicle: defaultVehicle }));
  }, [defaultVehicle]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/bookings", form);
      toast.success("Tack! Vi återkommer så snart vi kan med en bekräftad visningstid.");
      setForm({ name: "", email: "", phone: "", vehicle: "", preferred_date: "", message: "" });
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="booking-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="booking-name" className="text-slate-300">Namn *</Label>
          <Input
            id="booking-name"
            data-testid="booking-name-input"
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Ditt namn"
            className={inputCls}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="booking-phone" className="text-slate-300">Telefon</Label>
          <Input
            id="booking-phone"
            data-testid="booking-phone-input"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="07X-XXX XX XX"
            className={inputCls}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="booking-email" className="text-slate-300">E-post *</Label>
        <Input
          id="booking-email"
          data-testid="booking-email-input"
          type="email"
          required
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder="din@epost.se"
          className={inputCls}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-slate-300">Fordon</Label>
          <Select value={form.vehicle || undefined} onValueChange={(v) => set("vehicle", v)}>
            <SelectTrigger data-testid="booking-vehicle-select" className={`${inputCls} w-full`}>
              <SelectValue placeholder={form.vehicle || "Välj fordon (valfritt)"} />
            </SelectTrigger>
            <SelectContent className="bg-surface border-metallic text-white">
              {vehicles.map((v) => (
                <SelectItem key={v.id} value={v.title} data-testid={`booking-vehicle-option-${v.id}`}>
                  {v.title}
                </SelectItem>
              ))}
              <SelectItem value="Annat fordon">Annat fordon</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="booking-date" className="text-slate-300">Önskat datum</Label>
          <Input
            id="booking-date"
            data-testid="booking-date-input"
            type="date"
            value={form.preferred_date}
            onChange={(e) => set("preferred_date", e.target.value)}
            className={inputCls}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="booking-message" className="text-slate-300">Meddelande</Label>
        <Textarea
          id="booking-message"
          data-testid="booking-message-input"
          rows={3}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          placeholder="Berätta gärna kort vad du är intresserad av"
          className={inputCls}
        />
      </div>
      <Button
        type="submit"
        data-testid="booking-submit-button"
        disabled={sending}
        className="w-full rounded-full bg-brand hover:bg-brand-dark text-white font-semibold py-3"
      >
        <CalendarCheck size={16} className="mr-2" />
        {sending ? "Skickar..." : "Skicka bokningsförfrågan"}
      </Button>
    </form>
  );
}
